import type { Namespace, TFunction } from "i18next";

/**
 * Here rather than on either screen, because a korps and an ondersteuner are shown the same
 * number from the same store (ADR-0053) - and two copies of the rounding is how two screens
 * quietly stop agreeing about a drive.
 */

/**
 * An uncomputed pair sorts last rather than nearest: absent means unknown (ADR-0013), so it
 * cannot stand in for zero.
 */
export function reistijdSortering(seconds: number | string | null | undefined) {
  return seconds === null || seconds === undefined
    ? Number.MAX_SAFE_INTEGER
    : Number(seconds);
}

/**
 * The reistijd as a human reads it: a rounded drive, never the seconds the planner sorts on.
 * Absent is said - never a zero, and never a dash that reads as *nearby* (ADR-0053).
 */
export function reistijdLabel(
  t: TFunction<Namespace>,
  seconds: number | string | null | undefined,
) {
  if (seconds === null || seconds === undefined) {
    return t("common:reistijd.onbekend");
  }

  const minuten = Math.round(Number(seconds) / 60);
  if (minuten < 60) {
    return t("common:reistijd.minuten", { minuten });
  }

  const uren = Math.floor(minuten / 60);
  const rest = minuten % 60;
  return rest === 0
    ? t("common:reistijd.uren", { uren })
    : t("common:reistijd.urenMinuten", { uren, minuten: rest });
}

/** Which way a calendar is read: by date, or by how far away each dag is. */
export type Reistijdsortering = "datum" | "reistijd";

/**
 * A calendar in the order the reader asked for. The ISO date is the fallback within equal
 * reistijden, and the whole of the datum ordering - lexicographic and chronological are the
 * same thing there.
 */
export function gesorteerdOpReistijd<
  T extends { date: string; reistijdSeconds?: number | string | null },
>(dagen: readonly T[], sortering: Reistijdsortering): T[] {
  if (sortering === "datum") {
    return [...dagen].sort((a, b) => a.date.localeCompare(b.date));
  }

  return [...dagen].sort(
    (a, b) =>
      reistijdSortering(a.reistijdSeconds) -
        reistijdSortering(b.reistijdSeconds) || a.date.localeCompare(b.date),
  );
}
